import * as React from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import EventDetailsCard from 'app/components/EventDetailsCard';
import EventPageSkeleton from 'app/components/Skeletons/EventPage/EventPageSkeleton';
import { VendorDetails } from 'app/components/VendorDetails/Loadable';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { selectVendorData } from 'store/vendorSlice/selectors';
import { StyledBackgroundImage } from 'styles/common-styled-components';
import { getEventIdFromSlug } from 'utils/events';
import { formatMoney } from 'utils/formatMoney';
import { getTaxPrice } from 'utils/tickets';
import oveitLogo from '../../../assets/common/oveit-logo.svg';
import { Button } from '../../components/ui/button';
import { fetchEvent } from './slice';
import { selectEventPageData } from './slice/selectors';
import HeroBackgroundSkeleton from 'app/components/Skeletons/EventPage/HeroBackgroundSkeleton';

const EventDetailsPage: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { eventSlug } = useParams();
  const eventId = getEventIdFromSlug(eventSlug);
  const {
    event: currentEvent,
    event: {
      name,
      cover,
      gallery,
      minmaxticketsprices,
      currency,
      description,
      tax_included,
      tax_rate,
    },
    loading,
  } = useSelector(selectEventPageData);
  const vendorInfo = useSelector(selectVendorData);

  const eventHasImageHero = cover || gallery.length > 0;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const dispatchData = await dispatch(fetchEvent(eventId));
      if (!dispatchData?.payload?.id) {
        navigate('/404');
        return;
      }
    };
    fetchData();
  }, [navigate, dispatch, eventId]);

  const pageTitle = !name
    ? ''
    : t('eventDetailsPage.pageTitle', { eventName: name });

  let pageDescription = '';

  if (description) {
    pageDescription = description.slice(0, 150);
  } else {
    let lowestPrice = t('eventDetailsPage.pageDescriptionPriceInfo') + ' ';
    let minPrice = minmaxticketsprices?.minPrice;

    if (minPrice) {
      if (tax_included) {
        minPrice += getTaxPrice(minPrice, tax_rate.rate);
      }

      lowestPrice += formatMoney(minPrice, currency);
    } else {
      lowestPrice += `${formatMoney(0, currency)}`;
    }

    pageDescription = `${t('eventDetailsPage.pageDescription', {
      eventName: name,
    })} ${lowestPrice}`;
  }

  let pageImage = '';

  if (cover) {
    pageImage = cover.original;
  } else if (gallery.length) {
    pageImage = gallery[0].original;
  } else {
    pageImage = oveitLogo;
  }

  return (
    <>
      {loading ? (
        <HeroBackgroundSkeleton />
      ) : currentEvent.cover?.original ? (
        <div className="overflow-hidden w-full absolute top-0 left-0 hidden md:block">
          <StyledBackgroundImage
            image={currentEvent.cover!.original}
            className="w-full min-h-[200px] sm:min-h-[460px] bg-center bg-cover blur-[20px] scale-125"
          />
        </div>
      ) : null}
      <div className="page-container event-details-page relative">
        <Helmet>
          {loading ? null : <title>{pageTitle}</title>}
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={window.location.href} />
          {<meta property="og:image" content={pageImage} />}
        </Helmet>
        {loading ? null : (
          <Button
            asChild
            size="sm"
            variant="outline"
            className={`opacity-70 hover:opacity-100 z-10 hidden ${
              eventHasImageHero
                ? 'md:flex md:absolute md:-top-12 md:left-6 xl:top-0 xl:left-6'
                : 'xl:flex xl:absolute xl:top-6 xl:left-6'
            }`}
          >
            <Link to={`/org/${vendorInfo.id}`}>
              <ChevronLeft />
              {t('eventDetailsPage.more')}
            </Link>
          </Button>
        )}
        <div
          className={`section-container max-w-[1100px]! ${
            eventHasImageHero || loading ? 'pt-0!' : ''
          }`}
        >
          {loading ? (
            <EventPageSkeleton />
          ) : (
            <EventDetailsCard event={currentEvent} />
          )}
        </div>
        <React.Suspense fallback={<></>}>
          <div className="max-w-[1100px] mx-auto">
            <VendorDetails
              id={vendorInfo.id}
              publicInformation={vendorInfo.publicInformation}
              displayLinkToHomePage={true}
              isLoading={loading}
            />
          </div>
        </React.Suspense>
      </div>
    </>
  );
};

export default EventDetailsPage;
